/**
 * How many canvas pixels to draw per CSS pixel (UI-004).
 *
 * This used to be `devicePixelRatio` capped at 2, which had two problems. A pinch zoom
 * does not change `devicePixelRatio` at all, so pinching in magnified a bitmap drawn
 * for the unzoomed page — soft and blocky, like a stretched JPEG. And the cap blurred
 * any screen or browser zoom beyond 2x. The owner lifted the cap (2026-09-25).
 *
 * What the cap protected was fill cost: every frame blits the whole table layer. So
 * the limit is now on what that cost actually depends on, the number of buffer
 * pixels, not on the ratio. A small canvas can be very sharp, and a huge one stays
 * affordable.
 */

/** About 3x a 1080p screen. Enough for a full-screen table at 2x on a 4K display. */
export const MAX_BUFFER_PIXELS = 12_000_000;

/** Beyond this nobody can see a difference, and canvas size limits get close. */
export const MAX_RESOLUTION = 8;

export function renderResolution(
  cssWidth: number,
  cssHeight: number,
  devicePixelRatio: number,
  pinchScale: number,
): number {
  const dpr = Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1;
  const pinch = Number.isFinite(pinchScale) && pinchScale > 0 ? pinchScale : 1;
  const wanted = Math.max(1, dpr * pinch);

  const area = Math.max(1, cssWidth) * Math.max(1, cssHeight);
  const budget = Math.sqrt(MAX_BUFFER_PIXELS / area);

  return Math.max(1, Math.min(wanted, budget, MAX_RESOLUTION));
}
